// Editor für Notizen am Lead (Runde 7B, L80/L81/L69): Formatleiste wie in Close (Fett, Kursiv, Unterstrichen, Durchgestrichen, Marker,
// Link, Aufzählung, Nummerierung, Zitat), Eingabe im Text selbst. Mit „/“ öffnet sich ein Menü (Textbausteine aus Einstellungen ›
// Textbausteine, Aufzählung, Nummerierung, Zitat, Link), mit „@“ die Erwähnung eines Nutzers (wie bisher, die Glocke erkennt `@Vorname`).
// Eingefügt wird immer reiner Text. Der Wert ist HTML in der kleinen, sicheren Teilmenge von `werkzeuge/html.ts` (keine Stile,
// keine Bilder); Absätze sind `<p>`. Der Editor kommt auch beim Bearbeiten einer Notiz im Verlauf zum Einsatz: die Formatierung bleibt.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { Baustein } from "../../kommunikation/Editor";
import { useMeta } from "../../daten/meta";
import { erwaehnungText, erwaehnungTreffer } from "../../werkzeuge/erwaehnung";
import { sicheresHtml, textZuHtml } from "../../werkzeuge/html";
import { Avatar } from "../Bausteine";
import { Icon } from "../Icon";
import { TL } from "./leadTexte";
import { markerZuMark, markZuSpan } from "./notizHtml";
import "./notizEditor.css";

interface Props {
  /** HTML (oder einfacher Text eines älteren Entwurfs) */
  wert: string;
  onChange: (html: string) => void;
  platzhalter?: string;
  autoFokus?: boolean;
  /** ⌘/Strg + Enter */
  onStrgEnter?: () => void;
  bausteine?: Baustein[];
  ariaLabel?: string;
  mindesthoehe?: number;
}

const BLOCK = new Set(["P", "DIV", "UL", "OL", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "PRE", "TABLE", "HR"]);

/** HTML aus dem Editor: Absätze als `<p>`, nur die sichere Teilmenge. Leer, wenn kein Text da ist. */
export function notizHtml(roh: string): string {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${roh}</body>`, "text/html");
  const teile: string[] = [];
  let lose: string[] = [];
  const spuelen = () => {
    const innen = lose.join("");
    if (innen.replace(/<br\s*\/?>|&nbsp;|\s/gi, "")) teile.push(`<p>${innen}</p>`);
    lose = [];
  };
  for (const knoten of Array.from(doc.body.childNodes)) {
    if (knoten.nodeType === Node.ELEMENT_NODE && BLOCK.has((knoten as Element).tagName)) {
      spuelen();
      const el = knoten as Element;
      const leer = !(el.textContent ?? "").trim() && !el.querySelector("img,hr,li,br");
      if (leer) continue; // Rest des Browsers ohne Inhalt
      if (el.tagName === "DIV") teile.push(`<p>${el.innerHTML}</p>`);
      else teile.push(el.outerHTML);
    } else if (knoten.nodeType === Node.ELEMENT_NODE) lose.push((knoten as Element).outerHTML);
    else if (knoten.nodeType === Node.TEXT_NODE) lose.push((knoten.textContent ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"));
  }
  spuelen();
  const sauber = sicheresHtml(markerZuMark(teile.join("")));
  return (new DOMParser().parseFromString(sauber, "text/html").body.textContent ?? "").trim() ? markZuSpan(sauber) : "";
}

const anzeige = (wert: string) => sicheresHtml(markerZuMark(/<[a-z][\s\S]*>/i.test(wert) ? wert : textZuHtml(wert)));

function linkZiel(roh: string): string | null {
  const t = roh.trim();
  if (!t) return null;
  if (/^(https?:|mailto:|tel:)/i.test(t)) return t;
  if (/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(t)) return `mailto:${t}`;
  if (/^[^\s:]+\.[^\s:]{2,}(\/.*)?$/.test(t)) return `https://${t}`;
  return null;
}

interface MenueEintrag {
  schluessel: string;
  label: string;
  gruppe?: string;
  symbol?: ReactNode;
  tun: () => void;
}

interface Menue {
  art: "slash" | "at";
  query: string;
  knoten: Text;
  start: number;
}

export function NotizEditor({ wert, onChange, platzhalter, autoFokus, onStrgEnter, bausteine = [], ariaLabel = "Notiz", mindesthoehe = 84 }: Props) {
  const { nutzerListe } = useMeta();
  const rahmen = useRef<HTMLDivElement>(null);
  const flaeche = useRef<HTMLDivElement>(null);
  const zuletzt = useRef("");
  const auswahl = useRef<Range | null>(null);
  const [linkOffen, setLinkOffen] = useState(false);
  const [link, setLink] = useState("");
  const [linkFehler, setLinkFehler] = useState(false);
  const [menue, setMenue] = useState<Menue | null>(null);
  const [aktiv, setAktiv] = useState(0);
  const menueZu = useRef<string | null>(null);

  useLayoutEffect(() => {
    const el = flaeche.current;
    if (!el) return;
    document.execCommand("defaultParagraphSeparator", false, "p");
    el.innerHTML = anzeige(wert);
    zuletzt.current = notizHtml(el.innerHTML);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wert von außen (anderer Entwurf, Zurücksetzen nach dem Speichern)
  useEffect(() => {
    const el = flaeche.current;
    if (!el || wert === zuletzt.current) return;
    el.innerHTML = anzeige(wert);
    zuletzt.current = notizHtml(el.innerHTML);
  }, [wert]);

  useEffect(() => {
    if (autoFokus) flaeche.current?.focus();
  }, [autoFokus]);

  // Auswahl laufend merken (auch bei Eingabe ohne Tastenereignis, z. B. Diktat oder Einfügen)
  useEffect(() => {
    const merken = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && flaeche.current?.contains(sel.anchorNode)) auswahl.current = sel.getRangeAt(0).cloneRange();
    };
    document.addEventListener("selectionchange", merken);
    return () => document.removeEventListener("selectionchange", merken);
  }, []);

  const melden = () => {
    const el = flaeche.current;
    if (!el) return;
    if (/^(<br>|<p><br><\/p>|<div><br><\/div>)?$/.test(el.innerHTML.trim())) el.innerHTML = "";
    const aus = notizHtml(el.innerHTML);
    zuletzt.current = aus;
    onChange(aus);
  };

  const merkeAuswahl = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && flaeche.current?.contains(sel.anchorNode)) auswahl.current = sel.getRangeAt(0).cloneRange();
  };
  const auswahlZurueck = () => {
    const sel = window.getSelection();
    // Steht die Auswahl noch im Editor (Knöpfe nehmen den Fokus nicht), gilt sie; sonst die gemerkte (Link-Feld, Menü)
    if (sel && sel.rangeCount > 0 && flaeche.current?.contains(sel.anchorNode)) return;
    flaeche.current?.focus();
    const r = auswahl.current;
    if (!r) return;
    sel?.removeAllRanges();
    sel?.addRange(r);
  };
  const befehl = (name: string, arg?: string) => {
    auswahlZurueck();
    document.execCommand(name, false, arg);
    melden();
    merkeAuswahl();
  };
  const einfuegenHtml = (html: string) => {
    auswahlZurueck();
    document.execCommand("insertHTML", false, sicheresHtml(html));
    melden();
    merkeAuswahl();
  };

  // Marker wie Fett: ohne Auswahl schaltet er für den nächsten Text um, mit Auswahl wirkt er auf sie (`hiliteColor` des Browsers)
  const markerUmschalten = () => {
    auswahlZurueck();
    document.execCommand("styleWithCSS", false, "true");
    const aktuell = document.queryCommandValue("hiliteColor") || document.queryCommandValue("backColor") || "";
    document.execCommand("hiliteColor", false, /255,\s*229,\s*138/.test(aktuell) ? "transparent" : "#ffe58a");
    document.execCommand("styleWithCSS", false, "false");
    melden();
    merkeAuswahl();
  };

  const zitatUmschalten = () => {
    auswahlZurueck();
    const sel = window.getSelection();
    const el = sel?.anchorNode ? (sel.anchorNode.nodeType === Node.ELEMENT_NODE ? (sel.anchorNode as Element) : sel.anchorNode.parentElement) : null;
    document.execCommand("formatBlock", false, el?.closest("blockquote") ? "p" : "blockquote");
    melden();
    merkeAuswahl();
  };

  const linkSetzen = () => {
    const ziel = linkZiel(link);
    if (!ziel) {
      setLinkFehler(true);
      return;
    }
    auswahlZurueck();
    const sel = window.getSelection();
    if (sel && sel.isCollapsed) {
      const text = ziel.replace(/^(mailto:|tel:|https?:\/\/)/i, "");
      document.execCommand("insertHTML", false, `<a href="${ziel.replace(/"/g, "%22")}">${text.replace(/</g, "&lt;")}</a>`);
    } else document.execCommand("createLink", false, ziel);
    melden();
    setLinkOffen(false);
    setLink("");
    setLinkFehler(false);
  };

  // --- „/“- und „@“-Menü ------------------------------------------------------------------------------------------------------
  const eintraege = useMemo<MenueEintrag[]>(() => {
    if (!menue) return [];
    const q = menue.query.toLocaleLowerCase("de");
    if (menue.art === "at") {
      const alle = nutzerListe.filter((u) => u.is_active);
      return erwaehnungTreffer(nutzerListe, menue.query).map((u) => ({
        schluessel: `u-${u.id}`,
        label: u.name,
        symbol: <Avatar name={u.name} id={u.id} groesse={18} />,
        tun: () => einsetzenText(`@${erwaehnungText(u, alle)} `),
      }));
    }
    const liste: MenueEintrag[] = [
      ...bausteine.map((b) => ({ schluessel: `b-${b.id}`, label: b.name, gruppe: TL.notiz.baustein, tun: () => einfuegenHtml(b.html) })),
      { schluessel: "ul", label: "Aufzählung", gruppe: "Format", tun: () => befehl("insertUnorderedList") },
      { schluessel: "ol", label: "Nummerierte Liste", gruppe: "Format", tun: () => befehl("insertOrderedList") },
      { schluessel: "zitat", label: "Zitat", gruppe: "Format", tun: () => zitatUmschalten() },
      { schluessel: "link", label: "Link", gruppe: "Einfügen", tun: () => setLinkOffen(true) },
    ];
    return liste.filter((e) => !q || e.label.toLocaleLowerCase("de").includes(q) || (e.gruppe ?? "").toLocaleLowerCase("de").includes(q)).slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menue, bausteine, nutzerListe]);

  const einsetzenText = (text: string) => {
    auswahlZurueck();
    document.execCommand("insertText", false, text);
    melden();
    merkeAuswahl();
  };

  const menuePruefen = () => {
    const sel = window.getSelection();
    const el = flaeche.current;
    if (!sel || !sel.rangeCount || !el || !el.contains(sel.anchorNode) || !sel.isCollapsed) return setMenue(null);
    const r = sel.getRangeAt(0);
    if (r.startContainer.nodeType !== Node.TEXT_NODE) return setMenue(null);
    const davor = (r.startContainer.textContent ?? "").slice(0, r.startOffset);
    if (menueZu.current === davor) return setMenue(null);
    const at = /(?:^|[\s(])@([^\s@]{0,30})$/.exec(davor);
    const slash = /(?:^|\s)\/([^\s/]{0,30})$/.exec(davor);
    const m = at ? { art: "at" as const, q: at[1] } : slash ? { art: "slash" as const, q: slash[1] } : null;
    if (!m) return setMenue(null);
    setMenue({ art: m.art, query: m.q, knoten: r.startContainer as Text, start: r.startOffset - m.q.length - 1 });
    setAktiv(0);
  };

  const waehlen = (e: MenueEintrag) => {
    if (!menue) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount) {
      const r = sel.getRangeAt(0).cloneRange();
      try {
        r.setStart(menue.knoten, menue.start); // das „/“ oder „@“ samt dem getippten Stück
        sel.removeAllRanges();
        sel.addRange(r);
        document.execCommand("delete");
        merkeAuswahl();
      } catch {
        /* Text hat sich geändert: nichts löschen */
      }
    }
    setMenue(null);
    e.tun();
  };

  const tasten = (e: KeyboardEvent<HTMLDivElement>) => {
    if (menue && eintraege.length > 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setAktiv((a) => (a + (e.key === "ArrowDown" ? 1 : eintraege.length - 1)) % eintraege.length);
        return;
      }
      if ((e.key === "Enter" && !e.metaKey && !e.ctrlKey) || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        waehlen(eintraege[Math.min(aktiv, eintraege.length - 1)]);
        return;
      }
    }
    if (menue && e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      const sel = window.getSelection();
      menueZu.current = sel?.anchorNode ? (sel.anchorNode.textContent ?? "").slice(0, sel.anchorOffset) : null;
      setMenue(null);
      return;
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.shiftKey && onStrgEnter) {
      e.preventDefault();
      e.stopPropagation();
      onStrgEnter();
    }
  };

  const knopf = (titel: string, inhalt: ReactNode, onClick: () => void, extra = "") => (
    <button type="button" className={`symbolknopf symbolknopf--klein nt__knopf ${extra}`.trim()} title={titel} aria-label={titel} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {inhalt}
    </button>
  );

  return (
    <div className="nt erwaehnung-rahmen" ref={rahmen}>
      <div className="nt__leiste" role="toolbar" aria-label="Formatierung">
        {knopf("Fett (⌘B)", "B", () => befehl("bold"), "nt__fett")}
        {knopf("Kursiv (⌘I)", "I", () => befehl("italic"), "nt__kursiv")}
        {knopf("Unterstrichen (⌘U)", "U", () => befehl("underline"), "nt__unter")}
        {knopf("Durchgestrichen", "S", () => befehl("strikeThrough"), "nt__durch")}
        {knopf(TL.notiz.marker, <span className="nt__markerzeichen">A</span>, markerUmschalten)}
        <span className="nt__trenner" aria-hidden="true" />
        <button
          type="button"
          className="symbolknopf symbolknopf--klein nt__knopf"
          title="Link einfügen"
          aria-label="Link einfügen"
          aria-expanded={linkOffen}
          onMouseDown={(e) => {
            e.preventDefault();
            merkeAuswahl();
          }}
          onClick={() => setLinkOffen((o) => !o)}
        >
          <Icon name="link" groesse={14} />
        </button>
        {knopf("Aufzählung", <span aria-hidden="true">•≡</span>, () => befehl("insertUnorderedList"))}
        {knopf("Nummerierte Liste", <span aria-hidden="true">1.</span>, () => befehl("insertOrderedList"))}
        {knopf("Zitat", <span aria-hidden="true">“</span>, zitatUmschalten)}
        {knopf(TL.notiz.formatEntfernen, <span aria-hidden="true">T<sub>x</sub></span>, () => befehl("removeFormat"))}
        {bausteine.length > 0 ? <span className="nt__hinweis">„/“ fügt Textbausteine ein</span> : null}
      </div>
      {linkOffen ? (
        <div className="nt__link">
          <input
            className={`eingabe eingabe--klein${linkFehler ? " eingabe--fehler" : ""}`}
            placeholder="https://… oder name@firma.de"
            value={link}
            aria-label="Adresse des Links"
            autoFocus
            onChange={(e) => {
              setLink(e.target.value);
              setLinkFehler(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                linkSetzen();
              }
              if (e.key === "Escape") {
                e.stopPropagation();
                setLinkOffen(false);
              }
            }}
          />
          <button type="button" className="knopf knopf--klein" onClick={linkSetzen}>
            Übernehmen
          </button>
          {linkFehler ? <span className="nt__fehler">Das ist keine Adresse.</span> : null}
        </div>
      ) : null}
      <div
        ref={flaeche}
        className="nt__flaeche"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-platzhalter={platzhalter ?? ""}
        // `placeholder` ist an einem div kein Standard, Tests und Hilfstechnik finden das Feld aber darüber
        {...({ placeholder: platzhalter ?? "" } as object)}
        style={{ minHeight: mindesthoehe }}
        onInput={() => {
          melden();
          menuePruefen();
        }}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          if (text) document.execCommand("insertText", false, text);
        }}
        onBlur={() => {
          merkeAuswahl();
          window.setTimeout(() => setMenue(null), 150); // Klick auf einen Menüpunkt soll noch ankommen
        }}
        onKeyDown={tasten}
        onKeyUp={(e) => {
          merkeAuswahl();
          if (!["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) menuePruefen();
        }}
        onMouseUp={() => {
          merkeAuswahl();
          menuePruefen();
        }}
      />
      {menue && eintraege.length > 0 ? (
        <div className="erwaehnung nt__menue" role="listbox" aria-label={menue.art === "at" ? "Nutzer erwähnen" : "Einfügen"}>
          {eintraege.map((e, i) => (
            <button
              key={e.schluessel}
              type="button"
              role="option"
              aria-selected={i === aktiv}
              className={`erwaehnung__zeile${i === aktiv ? " erwaehnung__zeile--aktiv" : ""}`}
              onMouseDown={(ev) => ev.preventDefault()}
              onMouseEnter={() => setAktiv(i)}
              onClick={() => waehlen(e)}
            >
              {e.symbol}
              <span className="erwaehnung__name">{e.label}</span>
              {e.gruppe ? <span className="nt__gruppe">{e.gruppe}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
